import { useEffect, useRef, useState } from 'react';

import { styled } from '@mui/material';
import {
  TOKEN_SCOPE,
  useDismissAllTasks,
  useDismissTask,
  useServiceAppState,
  useTaskListWithSSE,
} from '@kc-console/context';
import { useClickOutside } from '@kc-console/core';

import TaskManagerDetail from './TaskManagerDetail';
import TaskManagerSummary from './TaskManagerSummary';

enum ViewMode {
  FULL = 'full',
  MINI = 'mini',
  FLOATING = 'floating',
}

/** Mini view가 자동으로 Floating button으로 접히기까지의 노출 시간(ms). */
const AUTO_COLLAPSE_MS = 2000;

/**
 * 태스크 매니저 컨테이너 (우측 하단 고정).
 * @description
 * - `useTaskListWithSSE`로 목록 조회 + SSE 갱신을 함께 구독한다.
 * - 프로젝트 스코프에서만 노출한다. 태스크는 프로젝트 리소스 작업 이력이므로,
 *   글로벌(도메인/언스코프) 영역에서는 위젯을 그리지 않고 목록 조회·폴링도 걸지 않는다.
 *   알림 팝업이 `내 리소스` 탭을 비활성화하는 것과 동일한 기준이다(KIC-61820).
 * - 기본 노출 규칙은 단순하다: task가 있으면 그리고, 없으면 `null`(최초 빈 배열도 동일).
 *   컴포넌트는 마운트를 유지하므로 `null`이어도 SSE 구독은 계속 살아있다.
 * - 단, 사용자가 task를 직접 제거해 비운 경우엔 바로 사라지지 않고 full 뷰 empty 상태를 보여준다
 *   (`keepOpenWhenEmpty` 플래그). 이 empty 상태에서 chevron(접기)을 누르면 위젯이 완전히 사라진다.
 * - 3단계 뷰: floating(기본 휴지) ⇄ mini(중간) → full(펼침).
 *   - 신규 태스크 추가 / full에서 '내림' → mini로 2초 노출 후 자동으로 floating 복귀.
 *   - floating hover → mini로 확대, mouseleave → floating 복귀(2초 타이머는 hover 중 취소).
 *   - mini 전체 영역 클릭 → full.
 */
const TaskManagerPanel = () => {
  const {
    state: { scope },
  } = useServiceAppState();
  const isProjectScope = scope === TOKEN_SCOPE.PROJECT;

  const { tasks, counts, filterByTab, isLoading } = useTaskListWithSSE({
    enabled: isProjectScope,
  });

  const { mutate: dismissTask } = useDismissTask();
  const { mutate: dismissAllTasks } = useDismissAllTasks();

  const hasTasks = tasks.length > 0;

  const [view, setView] = useState<ViewMode>(ViewMode.FLOATING);
  const [keepOpenWhenEmpty, setKeepOpenWhenEmpty] = useState(false);

  // 신규 task 감지 effect에서 최신 view를 stale 없이 읽기 위한 미러 ref.
  const viewRef = useRef(view);
  viewRef.current = view;

  const collapseTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const clearCollapseTimer = () => {
    if (collapseTimer.current) {
      clearTimeout(collapseTimer.current);
      collapseTimer.current = undefined;
    }
  };

  // mini로 잠깐 노출한 뒤 2초 후 floating으로 자동 접기. hover 등으로 다시 호출되면 타이머를 리셋한다.
  const scheduleCollapseToFloating = () => {
    clearCollapseTimer();
    collapseTimer.current = setTimeout(() => setView(ViewMode.FLOATING), AUTO_COLLAPSE_MS);
  };

  useEffect(() => clearCollapseTimer, []);

  // 신규 task 도착 감지: 이전에 없던 id가 생기면 mini로 2초 노출 후 floating으로 접는다.
  // 최초 로드(첫 데이터 도착)도 신규로 취급해 잠깐 mini로 떴다가 접힌다. full로 펼쳐 보고 있으면 방해하지 않는다.
  const knownTaskIds = useRef<Set<string> | null>(null);

  useEffect(() => {
    const prevIds = knownTaskIds.current;
    const hasNew = prevIds === null ? tasks.length > 0 : tasks.some(task => !prevIds.has(task.id));
    knownTaskIds.current = new Set(tasks.map(task => task.id));

    if (hasNew && viewRef.current !== ViewMode.FULL) {
      setView(ViewMode.MINI);
      scheduleCollapseToFloating();
    }
    // scheduleCollapseToFloating은 매 렌더 재생성되지만 tasks 변화에만 반응하도록 tasks만 의존.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  const handleDismiss = (taskId: string) => {
    setKeepOpenWhenEmpty(true);
    dismissTask(taskId);
  };

  const handleDismissAll = () => {
    setKeepOpenWhenEmpty(true);
    dismissAllTasks();
  };

  // 접기(chevron): task가 없는 일시적 empty 상태면 위젯을 완전히 닫고, 아니면 mini로 2초 노출 후 floating.
  const handleCollapse = () => {
    if (!hasTasks) {
      clearCollapseTimer();
      setView(ViewMode.FLOATING);
      setKeepOpenWhenEmpty(false);
      return;
    }

    setView(ViewMode.MINI);
    scheduleCollapseToFloating();
  };

  // 바 hover: 자동 접기 타이머 취소. floating이면 mini로 확대(모핑).
  const handleBarMouseEnter = () => {
    clearCollapseTimer();
    if (view === ViewMode.FLOATING) {
      setView(ViewMode.MINI);
    }
  };

  // 바에서 마우스가 벗어나면 floating으로 복귀(모핑). full 상태에는 영향 없다.
  const handleBarMouseLeave = () => {
    clearCollapseTimer();
    setView(ViewMode.FLOATING);
  };

  // full(펼침) 뷰에서 패널 바깥을 클릭하면 chevron(접기)과 동일하게 내린다(notification과 동일 UX).
  // mousedown은 setTimeout(0) 뒤 등록되므로 mini 바 클릭으로 full을 여는 그 클릭엔 반응하지 않는다.
  const detailRef = useClickOutside<HTMLDivElement>({
    enabled: view === ViewMode.FULL,
    onClickOutside: handleCollapse,
  });

  // 글로벌(도메인/언스코프) 영역에서는 태스크 매니저를 노출하지 않는다.
  // 훅 호출 뒤에 두어 스코프가 바뀌어도 hooks 순서는 유지된다.
  if (!isProjectScope) {
    return null;
  }

  // 최초 빈 배열(또는 직접 제거가 아닌 이유로 비어있음)이면 아무것도 그리지 않는다.
  if (!hasTasks && !keepOpenWhenEmpty) {
    return null;
  }

  return (
    <Anchor isFull={view === ViewMode.FULL}>
      {view === ViewMode.FULL ? (
        <TaskManagerDetail
          ref={detailRef}
          counts={counts}
          filterByTab={filterByTab}
          isLoading={isLoading}
          onCollapse={handleCollapse}
          onDismiss={handleDismiss}
          onDismissAll={handleDismissAll}
        />
      ) : (
        <TaskManagerSummary
          counts={counts}
          collapsed={view === ViewMode.FLOATING}
          onExpand={() => {
            clearCollapseTimer();
            setView(ViewMode.FULL);
          }}
          onMouseEnter={handleBarMouseEnter}
          onMouseLeave={handleBarMouseLeave}
        />
      )}
    </Anchor>
  );
};

export default TaskManagerPanel;

// full: 우측 32 / 하단 32, mini·floating: 우측 56 / 하단 24. mini↔floating은 같은 좌표라 모핑에 영향 없다.
const Anchor = styled('div', { shouldForwardProp: prop => prop !== 'isFull' })<{
  isFull: boolean;
}>(
  ({ theme, isFull }) => `
  position: fixed;
  right: ${isFull ? '32px' : '56px'};
  bottom: ${isFull ? '32px' : '24px'};
  z-index: ${theme.zIndex.modal - 1};
`,
);
